import { useState } from 'react';
import { Platform, SafeAreaView, View, StyleSheet } from 'react-native';
import { Text } from '@/components/Text';
import ConfirmModal from '@/components/SharedComponents/ConfirmModal/ConfirmModal';
import colors from '@/config/colors';
import { updateConsent } from '../../utils/consents';
import { styles } from './styles';
import { logError } from '@/utils/logHandling';
import {
  useResponsiveFontSize,
  useResponsiveHeight,
  useResponsiveWidth,
} from '@/components/Util/reactResponsiveDimensions';

const digaClientErrorCodeConfig = {
  'error.digaCodes.expired': {
    header: 'Rezeptcode abgelaufen',
    body: 'Ungültiger Rezeptcode! Der eingegebene Rezeptcode ist abgelaufen. Bitte wenden Sie sich an Ihre Krankenkasse, um einen gültigen Code zu erhalten.',
  },
  'error.digaCodes.cancelled': {
    header: 'Rezeptcode storniert',
    body: 'Ungültiger Rezeptcode! Der eingegebene Rezeptcode wurde storniert. Bitte wenden Sie sich an Ihre Krankenkasse, um einen gültigen Code zu erhalten.',
  },
  'error.digaCodes.notFound': {
    header: 'Rezeptcode nicht gefunden',
    body: 'Ungültiger Rezeptcode! Der eingegebene Rezeptcode wurde nicht gefunden. Bitte prüfen Sie die Eingabe oder wenden Sie sich an Ihre Krankenkasse.',
  },
  'error.digaCodes.invalidFormat': {
    header: 'Anfrage oder Datei ungültig',
    body: [
      'Technischer Fehler! Bitte versuchen Sie es erneut. Sollte der Fehler weiterhin bestehen, wenden Sie sich bitte an unseren Kundenservice unter 089 24886 4440 oder per E-Mail an ',
      <Text style={styles.emailPopUp} key={'email'}>
        info@neolexon.de.
      </Text>,
    ],
  },
  'error.digaCodes.serverError': {
    header: 'Serverfehler',
    body: 'Technischer Fehler! Der Rezeptcode kann momentan nicht verarbeitet werden. Bitte versuchen Sie es später erneut.',
  },
  fallback: {
    header: 'Ungültiger Rezeptcode',
    body: [
      'Bitte überprüfen Sie den Rezeptcode und versuchen Sie es erneut. ' +
        'Probleme beim Einlösen? Bitte melden Sie sich unter +49 (0)89 24886 4440 oder per E-mail an ',
      <Text style={styles.emailPopUp} key={'email'}>
        info@neolexon.de.
      </Text>,
    ],
  },
};

export type DigaClientErrorCodeType = keyof typeof digaClientErrorCodeConfig;

interface DigaCodeModalProps {
  isModalVisible: boolean;
  setModalVisible: (visible: boolean) => void;
  isDeactivationPostponeConsentModalVisible: boolean;
  setDeactivationPostponeConsentModalVisible: (visible: boolean) => void;
  isModalErrorVisible: boolean;
  setModalErrorVisible: () => void;
  isModalErrorAPIVisible: boolean;
  setModalErrorAPIVisible: (visible: boolean) => void;
  isModalInternetErrorVisible: boolean;
  setModalInternetErrorVisible: (visible: boolean) => void;
  consentId: string | null;
  isSecondDigaCode: boolean;
  modalErrorAPIMessageKey: DigaClientErrorCodeType | null;
}

export function DigaMessages({
  isModalVisible,
  setModalVisible,

  isDeactivationPostponeConsentModalVisible,
  setDeactivationPostponeConsentModalVisible,

  isModalErrorVisible,
  setModalErrorVisible,

  isModalErrorAPIVisible,
  setModalErrorAPIVisible,

  isModalInternetErrorVisible,
  setModalInternetErrorVisible,

  consentId,

  isSecondDigaCode,

  modalErrorAPIMessageKey,
}: DigaCodeModalProps) {
  const errorCodeText =
    'Bitte überprüfen Sie den Rezeptcode und versuchen Sie es erneut. ' +
    'Probleme beim Einlösen? Bitte melden Sie sich unter +49 (0)89 24886 4440 oder per E-mail an ';

  const internetErrorText =
    'Keine Verbindung zum neolexon Server. ' +
    'Bitte überprüfen Sie Ihre Internetverbindung und versuchen Sie es erneut. ' +
    '\n' +
    'Wenn das Problem besteht, melden Sie sich unter +49 (0)89 24886 4440 oder per E-mail an ';

  const email = 'info@neolexon.de';

  const deactivationPostponeConsentErrorText =
    'Die Einwilligung konnte nicht aktualisiert werden. Bitte versuchen Sie es später erneut. Sie finden die Einwillung zur späteren Account-Deaktivierung auch in Ihrem Nutzeraccount.';
  const [deactivationPostponeError, setDeactivationPostponeError] =
    useState('');

  const responsiveFontSize = useResponsiveFontSize(1);
  const responsiveHeight = useResponsiveHeight(100);

  const isFullscreen = Platform.OS !== 'web' && responsiveHeight < 500;

  const getResponsiveFontSize = (factor: number) => responsiveFontSize * factor;

  let responsiveStyles = StyleSheet.create({
    successText: {
      fontSize: isFullscreen
        ? getResponsiveFontSize(2.2)
        : getResponsiveFontSize(1.9),
      color: colors.green,
      marginVertical: getResponsiveFontSize(0.5),
    },
    instructions: {
      fontSize: isFullscreen
        ? getResponsiveFontSize(2.1)
        : getResponsiveFontSize(1.7),
      marginVertical: getResponsiveFontSize(0.5),
    },
    errorText: {
      fontSize: isFullscreen
        ? getResponsiveFontSize(2.0)
        : getResponsiveFontSize(1.7),
      color: colors.red,
      marginVertical: getResponsiveFontSize(0.5),
    },
    container: { padding: useResponsiveWidth(2.4) },
  });

  async function updateDeactivationPostponeConsent(isConsentGiven: boolean) {
    try {
      if (consentId) {
        await updateConsent(consentId, isConsentGiven);
        return true;
      }
      return false;
    } catch (_error) {
      logError('Error updating deactivation postpone consent', _error);
      return false;
    }
  }

  return (
    <SafeAreaView>
      <ConfirmModal
        isVisible={isModalVisible}
        headerText={
          isSecondDigaCode ? 'Rezeptcode vorgeplant' : 'Rezeptcode eingelöst'
        }
        contentText={
          isSecondDigaCode
            ? 'Ihr Rezeptcode wurde erfolgreich vorgeplant.'
            : 'Ihr Rezeptcode wurde erfolgreich eingelöst.'
        }
        confirmText={'OK'}
        confirmAction={() => {
          setModalVisible(false);
        }}
        customHeight={15}
      />

      <ConfirmModal
        isVisible={isDeactivationPostponeConsentModalVisible}
        isFullscreen={isFullscreen}
        headerText={'Einwilligung zur Verlängerung der Account-Deaktivierung'}
        confirmAction={async () => {
          if (await updateDeactivationPostponeConsent(true)) {
            return setDeactivationPostponeConsentModalVisible(false);
          }
          setDeactivationPostponeError(deactivationPostponeConsentErrorText);
        }}
        cancelAction={async () => {
          await updateDeactivationPostponeConsent(false);
          setDeactivationPostponeConsentModalVisible(false);
        }}
        isContentComponent
        contentText={
          <View style={responsiveStyles.container}>
            <Text style={responsiveStyles.successText}>
              Ihr Rezeptcode wurde erfolgreich eingelöst.
            </Text>
            <Text style={responsiveStyles.instructions}>
              Ich willige ein, dass mein Account mit meinen Daten nach Ablauf
              der 90-tägigen Nutzungsdauer des Rezeptcodes für weitere 30 Tage
              bestehen bleibt. Auf diese Weise habe ich 30 Tage länger Zeit, bei
              Bedarf einen weiteren Rezeptcode direkt einzulösen und meinen
              Account weiterzunutzen. Sollte nach der Verlängerung von 30 Tagen
              kein Rezeptcode eingelöst worden sein, wird mein Account aufgrund
              von gesetzlichen Bestimmungen deaktiviert. Nach der Deaktivierung
              kann ich jederzeit eine Reaktivierung beantragen. Ich kann meine
              Einwilligung jederzeit widerrufen. Die Einwilligung ist optional.
            </Text>
            {!!deactivationPostponeError && (
              <Text style={responsiveStyles.errorText}>
                {deactivationPostponeError}
              </Text>
            )}
          </View>
        }
        confirmText={'Ich willige ein'}
        cancelText={'Account unmittelbar nach Ablauf des Codes deaktivieren'}
      />

      <ConfirmModal
        isVisible={isModalErrorVisible}
        headerText={'Ungültiger Rezeptcode'}
        customHeight={35}
        contentText={[
          errorCodeText,
          email && (
            <Text style={styles.emailPopUp} key={'email'}>
              {email}
            </Text>
          ),
        ]}
        confirmText={'OK'}
        confirmAction={() => setModalErrorVisible()}
      />

      <ConfirmModal
        isVisible={isModalErrorAPIVisible}
        headerText={
          modalErrorAPIMessageKey &&
          modalErrorAPIMessageKey in digaClientErrorCodeConfig
            ? digaClientErrorCodeConfig[modalErrorAPIMessageKey].header
            : digaClientErrorCodeConfig['fallback']?.header
        }
        customHeight={35}
        contentText={
          modalErrorAPIMessageKey &&
          modalErrorAPIMessageKey in digaClientErrorCodeConfig
            ? digaClientErrorCodeConfig[modalErrorAPIMessageKey].body
            : [
                errorCodeText,
                email && (
                  <Text style={styles.emailPopUp} key={'email'}>
                    {email}
                  </Text>
                ),
              ]
        }
        confirmText={'OK'}
        confirmAction={() => setModalErrorAPIVisible(false)}
      />

      <ConfirmModal
        isVisible={isModalInternetErrorVisible}
        headerText={'Fehler bei Einlösen des Rezeptcodes'}
        customHeight={35}
        contentText={[
          internetErrorText,
          <Text style={styles.emailPopUp} key={'email'}>
            {email}
          </Text>,
        ]}
        confirmText={'OK'}
        confirmAction={() => {
          setModalInternetErrorVisible(false);
        }}
      />
    </SafeAreaView>
  );
}
